// Manual review surface — table of low-rated turns with sortable columns and
// an after/before date window.
//
// Reads from GET /feedback. Filters: max_rating + since/until window. The
// backend orders authoritatively via order_by / order_dir; the SPA only owns
// form state.

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  listLowRated,
  type FeedbackOrderBy,
  type FeedbackOrderDir,
  type LowRatedTurn,
} from '../../lib/feedbackApi';
import { Message } from '../chat/Message';
import type { SourceDoc } from '../../types';

const MAX_RATING_OPTIONS = [
  { value: 1.5, label: '≤ 1.5 (worst)' },
  { value: 2, label: '≤ 2.0' },
  { value: 3, label: '≤ 3.0 (mediocre)' },
  { value: 5, label: 'All rated' },
];

interface SortOption {
  value: string;
  label: string;
  order_by: FeedbackOrderBy;
  order_dir: FeedbackOrderDir;
}

const SORT_OPTIONS: SortOption[] = [
  { value: 'updated_desc', label: 'Most recent updates', order_by: 'feedback_updated_at', order_dir: 'desc' },
  { value: 'updated_asc', label: 'Oldest updates', order_by: 'feedback_updated_at', order_dir: 'asc' },
  { value: 'created_desc', label: 'Most recent submissions', order_by: 'feedback_created_at', order_dir: 'desc' },
  { value: 'created_asc', label: 'Oldest submissions', order_by: 'feedback_created_at', order_dir: 'asc' },
  { value: 'rating_asc', label: 'Mean rating asc', order_by: 'mean_rating', order_dir: 'asc' },
  { value: 'rating_desc', label: 'Mean rating desc', order_by: 'mean_rating', order_dir: 'desc' },
];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function ratingCell(value: number | null): string {
  if (value == null) return '—';
  return '★'.repeat(value) + '☆'.repeat(5 - value);
}

function findSortValue(order_by: FeedbackOrderBy, order_dir: FeedbackOrderDir): string {
  return (
    SORT_OPTIONS.find((o) => o.order_by === order_by && o.order_dir === order_dir)?.value ??
    'updated_desc'
  );
}

interface FeedbackDetailProps {
  row: LowRatedTurn;
  onClose: () => void;
}

function FeedbackDetail({ row, onClose }: FeedbackDetailProps) {
  const sources = (row.sources || []) as unknown as SourceDoc[];
  return (
    <div className="malpedia-feedback-detail">
      <div className="malpedia-feedback-detail-header">
        <h2>Turn #{row.seq} — {row.chat_title || row.chat_id}</h2>
        <button type="button" className="malpedia-feedback-detail-close" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="malpedia-feedback-detail-meta">
        <span>Mean rating: <strong>{row.mean_rating?.toFixed(2) ?? '—'}</strong></span>
        <span>Model: {row.model_name ?? '—'}</span>
        <span>Latency: {row.latency_ms != null ? `${row.latency_ms} ms` : '—'}</span>
        <span>Created: {formatDate(row.feedback_created_at)}</span>
        <span>Submitted: {formatDate(row.feedback_updated_at)}</span>
      </div>
      <div className="malpedia-feedback-detail-ratings">
        <div>Relevance: {ratingCell(row.relevance)}</div>
        <div>Correctness: {ratingCell(row.correctness)}</div>
        <div>Completeness: {ratingCell(row.completeness)}</div>
      </div>
      {row.comment && (
        <div className="malpedia-feedback-detail-section">
          <h3>Comment</h3>
          <p>{row.comment}</p>
        </div>
      )}
      {row.what_went_well && (
        <div className="malpedia-feedback-detail-section">
          <h3>What worked</h3>
          <p>{row.what_went_well}</p>
        </div>
      )}
      {row.what_didnt && (
        <div className="malpedia-feedback-detail-section">
          <h3>What didn't</h3>
          <p>{row.what_didnt}</p>
        </div>
      )}
      <div className="malpedia-feedback-detail-section">
        <h3>Query</h3>
        <Message role="user" content={row.query} />
      </div>
      <div className="malpedia-feedback-detail-section">
        <h3>Answer</h3>
        <Message role="assistant" content={row.answer} sources={sources} />
      </div>
    </div>
  );
}

export default function FeedbackList() {
  const [maxRating, setMaxRating] = useState<number>(2.0);
  const [after, setAfter] = useState<string>('');
  const [before, setBefore] = useState<string>('');
  const [orderBy, setOrderBy] = useState<FeedbackOrderBy>('feedback_updated_at');
  const [orderDir, setOrderDir] = useState<FeedbackOrderDir>('desc');
  const [rows, setRows] = useState<LowRatedTurn[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<LowRatedTurn | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listLowRated({
      max_rating: maxRating,
      since: after || undefined,
      until: before || undefined,
      order_by: orderBy,
      order_dir: orderDir,
      limit: 200,
    })
      .then((data) => {
        if (cancelled) return;
        setRows(data);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof Error ? err.message : 'Failed to load feedback');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [maxRating, after, before, orderBy, orderDir]);

  const sortValue = findSortValue(orderBy, orderDir);

  const onSortChange = (value: string) => {
    const opt = SORT_OPTIONS.find((o) => o.value === value);
    if (!opt) return;
    setOrderBy(opt.order_by);
    setOrderDir(opt.order_dir);
  };

  const toggleUpdatedSort = () => {
    if (orderBy === 'feedback_updated_at') {
      setOrderDir(orderDir === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy('feedback_updated_at');
      setOrderDir('desc');
    }
  };

  const resetWindow = () => {
    setAfter('');
    setBefore('');
  };

  const updatedArrow =
    orderBy === 'feedback_updated_at' ? (orderDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div className="malpedia-feedback-page">
      <div className="malpedia-feedback-page-header">
        <h1>Feedback review</h1>
        <div className="malpedia-feedback-page-filters">
          <label>
            Max mean rating
            <select
              value={maxRating}
              onChange={(e) => setMaxRating(Number(e.target.value))}
            >
              {MAX_RATING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label>
            After
            <input
              type="date"
              value={after}
              onChange={(e) => setAfter(e.target.value)}
            />
          </label>
          <label>
            Before
            <input
              type="date"
              value={before}
              onChange={(e) => setBefore(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="malpedia-feedback-reset-window"
            onClick={resetWindow}
            disabled={!after && !before}
          >
            All time
          </button>
          <label>
            Sort
            <select value={sortValue} onChange={(e) => onSortChange(e.target.value)}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <span className="malpedia-feedback-page-count">
            {loading ? 'Loading…' : `${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`}
          </span>
        </div>
      </div>

      <table className="malpedia-feedback-table">
        <thead>
          <tr>
            <th>Mean</th>
            <th>Relevance</th>
            <th>Correctness</th>
            <th>Completeness</th>
            <th>Query</th>
            <th>Created</th>
            <th
              className="malpedia-feedback-sortable"
              onClick={toggleUpdatedSort}
              title="Click to sort by submission time"
            >
              Submitted{updatedArrow}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.turn_id}
              className="malpedia-feedback-row"
              onClick={() => setSelected(r)}
            >
              <td><strong>{r.mean_rating?.toFixed(2) ?? '—'}</strong></td>
              <td>{ratingCell(r.relevance)}</td>
              <td>{ratingCell(r.correctness)}</td>
              <td>{ratingCell(r.completeness)}</td>
              <td className="malpedia-feedback-row-query">{r.query}</td>
              <td>{formatDate(r.feedback_created_at)}</td>
              <td>{formatDate(r.feedback_updated_at)}</td>
            </tr>
          ))}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={7} className="malpedia-feedback-empty">
                No feedback in this window.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {selected && (
        <FeedbackDetail row={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
